import { AnimatePresence, motion, type TargetAndTransition, type Transition } from 'framer-motion';
import type { KeyboardEvent, PointerEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Text } from '@bonusly/design-system';

import { Bizy, type BizyState } from '../../bizy';
import type { BizyWhatsNewTip as BizyWhatsNewTipData } from '../hooks/use-bizy-whats-new-tip';
import { BizyAdminEmptyState } from './bizy-admin-empty-state';
import { BizyGameInvite } from './bizy-game-invite';
import { BizyWhatsNewTip } from './bizy-whats-new-tip';

interface BizyFreshStartGreetingProps {
	/** Admin first-launch intro replaces the mascot + greeting cluster. */
	showAdminIntro: boolean;
	fontSize: 'sm' | 'md';

	// What's New tip — replaces the mascot + greeting when active.
	showWhatsNewTip: boolean;
	whatsNewTip: BizyWhatsNewTipData | null;
	onSelectTipPrompt: (prompt: string) => void;

	// Mascot + easter-egg interactions (handlers shared with the chat view's
	// in-timeline Bizy).
	bizyState: BizyState;
	bizyAnchorRef?: Ref<HTMLDivElement>;
	isBizyHidden: boolean;
	holdSpinAnimation: TargetAndTransition;
	holdSpinTransition: Transition;
	showNotificationGreeting: boolean;
	isGamePromptOpen: boolean;
	onBizyClick: () => void;
	onBizyPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
	onBizyPointerEnd: () => void;
	onBizyKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
	onBizyKeyUp: (event: KeyboardEvent<HTMLDivElement>) => void;
	onGameInvitePlay: () => void;
	onGameInviteDismiss: () => void;

	// Greeting copy.
	greetingText: string;
	/** Trial companies on the home page get the trial welcome instead of the greeting line. */
	showTrialWelcome: boolean;
	displayName: string;
}

/**
 * The fresh-start greeting cluster (admin intro / What's New tip / mascot +
 * greeting). Rendered by the true empty state and, identically, inside the
 * idle blank-slate region below the fresh-start divider (BIZY-683).
 */
export function BizyFreshStartGreeting({
	showAdminIntro,
	fontSize,
	showWhatsNewTip,
	whatsNewTip,
	onSelectTipPrompt,
	bizyState,
	bizyAnchorRef,
	isBizyHidden,
	holdSpinAnimation,
	holdSpinTransition,
	showNotificationGreeting,
	isGamePromptOpen,
	onBizyClick,
	onBizyPointerDown,
	onBizyPointerEnd,
	onBizyKeyDown,
	onBizyKeyUp,
	onGameInvitePlay,
	onGameInviteDismiss,
	greetingText,
	showTrialWelcome,
	displayName,
}: BizyFreshStartGreetingProps) {
	const { t } = useTranslation('admin_bizy');

	return (
		<>
			{showAdminIntro && !showWhatsNewTip && <BizyAdminEmptyState fontSize={fontSize} />}

			{showWhatsNewTip && whatsNewTip ? (
				<Flex direction="column" align="center" justify="center" flex={1} py={6}>
					<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15 }}>
						<BizyWhatsNewTip tip={whatsNewTip} bizyAnchorRef={bizyAnchorRef} isBizyHidden={isBizyHidden} onSelectPrompt={onSelectTipPrompt} />
					</motion.div>
				</Flex>
			) : (
				<Flex direction="column" align="center" justify="center" flex={1} py={6} gap={4}>
					<motion.div
						ref={bizyAnchorRef}
						initial={{ scale: 0, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.25 }}
						style={{ visibility: isBizyHidden ? 'hidden' : 'visible', position: 'relative' }}
					>
						<motion.div animate={holdSpinAnimation} transition={holdSpinTransition} style={{ display: 'flex' }}>
							<motion.div
								// Clicking the empty-state Bizy with an unread engages
								// the proactive conversation. Otherwise a long press opens
								// the local game easter egg.
								onClick={onBizyClick}
								onPointerDown={onBizyPointerDown}
								onPointerUp={onBizyPointerEnd}
								onPointerCancel={onBizyPointerEnd}
								onKeyDown={onBizyKeyDown}
								onKeyUp={onBizyKeyUp}
								style={{ cursor: 'pointer', display: 'flex' }}
								role="button"
								tabIndex={0}
								aria-label={showNotificationGreeting ? t('sidePanel.openNewBizyMessage') : t('gameInvite.ariaLabel')}
							>
								<Bizy state={bizyState} size="md" />
							</motion.div>
						</motion.div>
						<AnimatePresence>
							{isGamePromptOpen && !showNotificationGreeting && (
								<motion.div
									key="empty-bizy-game-invite"
									initial={{ opacity: 0, x: '-50%', y: 6, scale: 0.96 }}
									animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
									exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.96 }}
									transition={{ duration: 0.18 }}
									style={{ position: 'absolute', left: '50%', bottom: 'calc(100% + 10px)', zIndex: 2, pointerEvents: 'auto' }}
								>
									<BizyGameInvite onPlay={onGameInvitePlay} onDismiss={onGameInviteDismiss} />
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>

					{!showAdminIntro && !showTrialWelcome && (
						<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.45 }}>
							<Text fontSize="sm" color="text.soft" textAlign="center" maxW="260px" lineHeight="tall">
								{greetingText}
							</Text>
						</motion.div>
					)}
					{showTrialWelcome && (
						<motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.55 }}>
							<Flex direction="column" gap={1} align="center" maxW="280px">
								<Text fontSize="sm" fontWeight="semibold" color="text.default" textAlign="center">
									{t('trialWelcome.title', { name: displayName })}
								</Text>
								<Text fontSize="sm" color="text.soft" textAlign="center" lineHeight="tall">
									{t('trialWelcome.body')}
								</Text>
							</Flex>
						</motion.div>
					)}
				</Flex>
			)}
		</>
	);
}
